import {
    MuiTableBody,
    Typography,
    MuiTableHead,
    MuiTable,
    MuiTableContainer,
    PargodaInfoIcon,
    useTheme,
    Box,
    useAppSelector,
    Icon,
    IconButton,
    SortDirections,
} from '../../../base';
import {
    DASHBOARD_TIME_RANGES,
    IClientTrackableDataObject,
    ISourceRow,
    TooltipPlacement,
} from '../../../common';
import { dashboardSelectors } from '../../../actions';
import {
    StyledWrapperCard,
    StyledCardHeader,
    StyledHeaderContent,
    StyledIconWrapper,
    StyledHeaderIcon,
    StyledSkeleton,
    StyledTableRow,
    StyledRankCell,
    StyledProgressBarContainer,
    StyledLinearProgress,
    StyledPercentageLabel,
    StyledLinkCell,
    StyledNumbersCell,
    StyledProgressCell,
    StyledHeaderRow,
} from './SourcesCard.styles';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';

// Sort field constants
const SORT_FIELDS = {
    ENTRIES: 'entries',
    APPLY: 'apply',
} as const;

type SortField = (typeof SORT_FIELDS)[keyof typeof SORT_FIELDS];

interface ISourcesSort {
    sortField: SortField;
    sortDirection: SortDirections | '';
}

interface Props {
    timeRange: string | null;
}

const SourcesCard = ({ timeRange }: Props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [activeSourceData, setActiveSourceData] = useState<Array<ISourceRow>>(
        []
    );
    const [sort, setSort] = useState<ISourcesSort>({
        sortField: SORT_FIELDS.APPLY,
        sortDirection: SortDirections.desc,
    });

    const sourcesData = useAppSelector(
        dashboardSelectors.getTrackableStatistics
    );
    const sourcesLoading = useAppSelector(
        dashboardSelectors.getIsLoadingTrackableStatistics
    );

    const sortSourcesData = (
        data: Array<ISourceRow>,
        sortField: SortField,
        sortDirection: SortDirections | ''
    ): Array<ISourceRow> => {
        if (!sortDirection) {
            return data;
        }

        const sortedData = [...data].sort((a, b) => {
            const aValue = a[sortField];
            const bValue = b[sortField];

            if (sortDirection === SortDirections.desc) {
                return bValue - aValue;
            } else {
                return aValue - bValue;
            }
        });

        // Update ranks after sorting
        return sortedData.map((item, index) => ({
            ...item,
            rank: index + 1,
        }));
    };

    useEffect(() => {
        const transformData = (
            data: IClientTrackableDataObject
        ): Array<ISourceRow> => {
            return Object.entries(data.not_public).map(
                ([link, stats], index) => ({
                    id: link,
                    link,
                    rank: index + 1,
                    entries: stats.entries,
                    apply: stats.applications,
                    percentage:
                        sort.sortField === SORT_FIELDS.ENTRIES
                            ? stats.percentOfEntries
                            : stats.percentOfApplications,
                })
            );
        };

        let transformedData: Array<ISourceRow> = [];
        switch (timeRange) {
            case DASHBOARD_TIME_RANGES.WEEK:
                transformedData = transformData(sourcesData.week);
                break;
            case DASHBOARD_TIME_RANGES.MONTH:
                transformedData = transformData(sourcesData.month);
                break;
            case DASHBOARD_TIME_RANGES.QUARTER:
                transformedData = transformData(sourcesData.quarter);
                break;
            case DASHBOARD_TIME_RANGES.YEAR:
                transformedData = transformData(sourcesData.year);
                break;
            default:
                transformedData = transformData(sourcesData.week);
                break;
        }

        // Apply sorting
        const sortedData = sortSourcesData(
            transformedData,
            sort.sortField,
            sort.sortDirection
        );
        setActiveSourceData(sortedData);
    }, [timeRange, sourcesData, sort]);

    const getSortIconName = (field: SortField): string => {
        if (sort.sortField === field && sort.sortDirection) {
            return sort.sortDirection === SortDirections.asc
                ? 'ExpandLessOutlinedIcon'
                : 'ExpandMoreOutlinedIcon';
        }
        return 'UnfoldMoreIcon';
    };

    const getProgressHeaderText = (): string => {
        if (sort.sortField === SORT_FIELDS.ENTRIES) {
            return t('dashboardPage.sourceTableProgressViews');
        } else {
            return t('dashboardPage.sourceTableProgressApplications');
        }
    };

    const handleSortChange = (field: SortField) => {
        if (sort.sortField !== field) {
            // Switching to a new field, default to descending
            setSort({
                sortField: field,
                sortDirection: SortDirections.desc,
            });
        } else {
            // Same field, cycle through: desc -> asc -> desc
            let nextDirection: SortDirections;
            if (sort.sortDirection === SortDirections.desc) {
                nextDirection = SortDirections.asc;
            } else {
                nextDirection = SortDirections.desc;
            }
            setSort({
                sortField: field,
                sortDirection: nextDirection,
            });
        }
    };

    const renderEmptyState = () => {
        return (
            <Box sx={{ p: 2, justifyItems: 'center' }}>
                <Icon name="NoTrackableDataIconBig" size={80} color="primary" />
                <Typography variant="h5" sx={{ pb: 4 }}>
                    {t('dashboardPage.noSourcesDataAvailable')}
                </Typography>
            </Box>
        );
    };

    return (
        <>
            {sourcesLoading ? (
                <StyledSkeleton variant="rectangular" height={210} />
            ) : (
                <StyledWrapperCard>
                    <StyledCardHeader>
                        <StyledHeaderContent>
                            <StyledIconWrapper edge="end" size="small" disabled>
                                <StyledHeaderIcon
                                    name={'MovingIcon'}
                                    size={28}
                                />
                            </StyledIconWrapper>
                            <Typography variant="h6">
                                {t('dashboardPage.sourcesDataTitle')}
                            </Typography>
                        </StyledHeaderContent>
                    </StyledCardHeader>

                    {activeSourceData.length === 0 ? (
                        renderEmptyState()
                    ) : (
                        <MuiTableContainer>
                            <MuiTable stickyHeader size="small">
                                <MuiTableHead>
                                    <StyledHeaderRow>
                                        <StyledRankCell>
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                            >
                                                <Typography variant="body1">
                                                    #
                                                </Typography>
                                                <PargodaInfoIcon
                                                    text={t(
                                                        'dashboardPage.sourceRanksInfo'
                                                    )}
                                                    placement={
                                                        TooltipPlacement.TOP_START
                                                    }
                                                    size={14}
                                                    wrapperSx={{
                                                        paddingInlineStart:
                                                            theme.spacing(0.5),
                                                    }}
                                                    color={
                                                        theme.palette.secondary
                                                            .main
                                                    }
                                                />
                                            </Box>
                                        </StyledRankCell>
                                        <StyledLinkCell>
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                            >
                                                <Typography variant="body2">
                                                    {t(
                                                        'dashboardPage.sourceTableLink'
                                                    )}
                                                </Typography>
                                                <PargodaInfoIcon
                                                    text={t(
                                                        'dashboardPage.sourceNameInfo'
                                                    )}
                                                    placement={
                                                        TooltipPlacement.TOP_START
                                                    }
                                                    size={14}
                                                    wrapperSx={{
                                                        paddingInlineStart:
                                                            theme.spacing(0.5),
                                                    }}
                                                    color={
                                                        theme.palette.secondary
                                                            .main
                                                    }
                                                />
                                            </Box>
                                        </StyledLinkCell>
                                        <StyledNumbersCell>
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                            >
                                                <Typography variant="body2">
                                                    {t(
                                                        'dashboardPage.sourceTableEntries'
                                                    )}
                                                </Typography>
                                                <PargodaInfoIcon
                                                    text={t(
                                                        'dashboardPage.sourceViewsInfo'
                                                    )}
                                                    placement={
                                                        TooltipPlacement.TOP_START
                                                    }
                                                    size={14}
                                                    wrapperSx={{
                                                        paddingInlineStart:
                                                            theme.spacing(0.5),
                                                    }}
                                                    color={
                                                        theme.palette.secondary
                                                            .main
                                                    }
                                                />
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        handleSortChange(
                                                            SORT_FIELDS.ENTRIES
                                                        )
                                                    }
                                                    sx={{
                                                        marginInlineStart: 0.5,
                                                        width: 16,
                                                        height: 16,
                                                        padding: 0,
                                                    }}
                                                >
                                                    <Icon
                                                        size={16}
                                                        name={getSortIconName(
                                                            SORT_FIELDS.ENTRIES
                                                        )}
                                                        color="action"
                                                    />
                                                </IconButton>
                                            </Box>
                                        </StyledNumbersCell>
                                        <StyledNumbersCell>
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                            >
                                                <Typography variant="body2">
                                                    {t(
                                                        'dashboardPage.sourceTableApply'
                                                    )}
                                                </Typography>
                                                <PargodaInfoIcon
                                                    text={t(
                                                        'dashboardPage.sourceApplicationsInfo'
                                                    )}
                                                    placement={
                                                        TooltipPlacement.TOP_START
                                                    }
                                                    size={14}
                                                    wrapperSx={{
                                                        paddingInlineStart:
                                                            theme.spacing(0.5),
                                                    }}
                                                    color={
                                                        theme.palette.secondary
                                                            .main
                                                    }
                                                />
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        handleSortChange(
                                                            SORT_FIELDS.APPLY
                                                        )
                                                    }
                                                    sx={{
                                                        marginInlineStart: 0.5,
                                                        width: 16,
                                                        height: 16,
                                                        padding: 0,
                                                    }}
                                                >
                                                    <Icon
                                                        size={16}
                                                        name={getSortIconName(
                                                            SORT_FIELDS.APPLY
                                                        )}
                                                        color="action"
                                                    />
                                                </IconButton>
                                            </Box>
                                        </StyledNumbersCell>
                                        <StyledProgressCell>
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                            >
                                                <Typography variant="body2">
                                                    {getProgressHeaderText()}
                                                </Typography>
                                                <PargodaInfoIcon
                                                    text={t(
                                                        'dashboardPage.sourceProgressInfo'
                                                    )}
                                                    placement={
                                                        TooltipPlacement.TOP_START
                                                    }
                                                    size={14}
                                                    wrapperSx={{
                                                        paddingInlineStart:
                                                            theme.spacing(0.5),
                                                    }}
                                                    color={
                                                        theme.palette.secondary
                                                            .main
                                                    }
                                                />
                                            </Box>
                                        </StyledProgressCell>
                                    </StyledHeaderRow>
                                </MuiTableHead>
                                <MuiTableBody>
                                    {activeSourceData.map((row: ISourceRow) => (
                                        <StyledTableRow key={row.id}>
                                            <StyledRankCell>
                                                {row.rank}
                                            </StyledRankCell>
                                            <StyledLinkCell
                                                component="th"
                                                scope="row"
                                            >
                                                {row.link}
                                            </StyledLinkCell>
                                            <StyledNumbersCell>
                                                {row.entries}
                                            </StyledNumbersCell>
                                            <StyledNumbersCell>
                                                {row.apply}
                                            </StyledNumbersCell>
                                            <StyledProgressCell>
                                                <StyledProgressBarContainer>
                                                    <StyledLinearProgress
                                                        variant="determinate"
                                                        value={row.percentage}
                                                    />
                                                    <StyledPercentageLabel>
                                                        {`${row.percentage}%`}
                                                    </StyledPercentageLabel>
                                                </StyledProgressBarContainer>
                                            </StyledProgressCell>
                                        </StyledTableRow>
                                    ))}
                                </MuiTableBody>
                            </MuiTable>
                        </MuiTableContainer>
                    )}
                </StyledWrapperCard>
            )}
        </>
    );
};

export default SourcesCard;
